/**
 * OptimizedImage - Komponent do szybkiego ładowania zdjęć
 * - Pokazuje skeleton podczas ładowania
 * - Retry przy błędzie
 * - Eager loading dla ważnych zdjęć
 * - Fallback placeholder
 */
import { useState, useEffect } from 'react';
import { getImageUrl, PLACEHOLDER_IMAGE } from '@/utils/imageUrl';

export default function OptimizedImage({ 
  src, 
  alt = '', 
  className = '',
  priority = false, // true = eager loading (główne zdjęcia)
  fallback = PLACEHOLDER_IMAGE,
  onClick,
  ...props 
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  const imageUrl = getImageUrl(src);
  const maxRetries = 2;
  
  // Reset state when src changes
  useEffect(() => {
    setLoaded(false);
    setError(false);
    setRetryCount(0);
  }, [src]);
  
  // Preload priority images
  useEffect(() => {
    if (priority && imageUrl) {
      const img = new Image();
      img.src = imageUrl;
    }
  }, [priority, imageUrl]);
  
  const handleLoad = () => {
    setLoaded(true);
    setError(false);
  };
  
  const handleError = () => {
    if (retryCount < maxRetries) {
      // Retry with cache bust
      setRetryCount(prev => prev + 1);
    } else {
      setError(true);
    }
  };
  
  // No src - show placeholder
  if (!imageUrl) {
    return (
      <div className={`bg-zinc-200 flex items-center justify-center ${className}`} {...props}>
        <img src={fallback} alt={alt} className="w-full h-full object-cover opacity-50" />
      </div>
    );
  }
  
  // Build final URL with retry cache bust
  const finalUrl = retryCount > 0 
    ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}retry=${retryCount}`
    : imageUrl;
  
  return (
    <div className={`relative overflow-hidden ${className}`} onClick={onClick} {...props}>
      {/* Skeleton loader */}
      {!loaded && !error && (
        <div className="absolute inset-0 bg-zinc-200 animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" 
               style={{ 
                 backgroundSize: '200% 100%',
                 animation: 'shimmer 1.5s infinite'
               }} />
        </div>
      )}
      
      {/* Actual image */}
      {!error ? (
        <img
          src={finalUrl}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      ) : (
        <img 
          src={fallback} 
          alt={alt} 
          className="w-full h-full object-cover"
        />
      )}
    </div>
  );
}

// Prostsza wersja bez skeleton - do małych miniaturek
export function SimpleImage({ src, alt = '', className = '', ...props }) {
  const [error, setError] = useState(false);
  const imageUrl = getImageUrl(src);
  
  if (!imageUrl || error) {
    return (
      <div className={`bg-zinc-200 ${className}`} {...props} />
    );
  }
  
  return (
    <img
      src={imageUrl}
      alt={alt}
      loading="lazy"
      onError={() => setError(true)}
      className={className}
      {...props}
    />
  );
}
